/**
 * macOS desktop — per board "macOS Desktop": the top-level folders as a menu
 * bar (cascading to any depth) plus a Window menu, the open screens as
 * DESKTOP WINDOWS, and a Dock.
 *
 * Windows: every open screen is a live window (it keeps its state while
 * another is in front); the current screen is the front one. Windows move by
 * their title bar, resize from any edge or corner, and TILE as on macOS —
 * drag to an edge for that half (the top edge fills), to a corner for that
 * quarter, or use the green button's menu / the Window menu (halves,
 * quarters, Fill, Arrange all, Return to previous size). Minimised windows
 * wait in the Dock. Positions are remembered on this device.
 *
 * Dock: left, bottom or right (drag it to an edge, right-click → Position on
 * screen, or Desktop & Dock settings), magnification on / off, and the desktop
 * picture (built-in or the user's own) — saved per user, per application, on
 * the server.
 *
 * The menu bar lives in the HOST's top bar (portaled into its centre slot), so
 * there is one bar, as on the real desktop; without a chrome provider it falls
 * back to a strip under the host bar. Open-editor instances are kept alive by
 * the shell itself, outside every layout.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { tw } from '../../testHooks'
import { useHasShellChrome, useShellChrome } from '../nav/chromeContext'
import { EmptyPane } from '../nav/parts/EmptyPane'
import { NavIcon } from '../nav/navIcons'
import { S } from '../nav/tokens'
import type { RunNavItem } from '../nav/types'
import { AllScreens, type AllScreensTab } from './desktopMac/AllScreens'
import { Dock } from './desktopMac/Dock'
import { MacWindow } from './desktopMac/MacWindow'
import { MacMenuBar, shownEditorOf, type WindowAction } from './desktopMac/MenuBar'
import { DOCK_SIZE_DEFAULT, DOCK_SIZE_MAX, DOCK_SIZE_MIN, MacSettings, type DockSide } from './desktopMac/Settings'
import { MAC_CSS, glass } from './desktopMac/styles'
import { wallpaperCss } from './desktopMac/wallpapers'
import {
  TILE_LABELS, arrange, clampRect, defaultRect, loadWindows, reflow, saveWindows, tileRect,
  type Rect, type TileKind, type WinState,
} from './desktopMac/windows'
import type { LayoutTopBarSlots, ShellLayoutProps } from './types'

const LID = 'desktop-mac'
/** Height of the fallback menu strip (no host chrome provider). */
const STRIP_HEIGHT = 40
/** Room the Dock takes beyond its tile size (padding, margin, indicator dots). */
const DOCK_CHROME = 38
/** Re-publish attempts for the menu-bar slot (see the effect). */
const PUBLISH_RETRIES = 20
const SIDES: DockSide[] = ['bottom', 'left', 'right']

export default function DesktopMacLayout({
  model, appTitle, themeColor, compact = false, renderObject, headerExtras,
  emptyNavMessage, emptyPaneMessage, setTopBarSlots,
}: ShellLayoutProps) {
  const accent = themeColor || S.accent
  const full = model.fullScreen
  const current = model.current
  const shownEditor = shownEditorOf(model)

  // ── The user's desktop settings (server-saved with their prefs) ─────
  const sidePref = model.layoutPref<string>(LID, 'dockSide', 'bottom')
  const dockSide: DockSide = (SIDES as string[]).includes(sidePref) ? sidePref as DockSide : 'bottom'
  const magnify = model.layoutPref<boolean>(LID, 'magnify', true) !== false
  const sizePref = Number(model.layoutPref<number>(LID, 'dockSize', DOCK_SIZE_DEFAULT))
  const dockSize = Number.isFinite(sizePref) ? Math.max(DOCK_SIZE_MIN, Math.min(DOCK_SIZE_MAX, Math.round(sizePref))) : DOCK_SIZE_DEFAULT
  const DOCK_RESERVE = dockSize + DOCK_CHROME
  const picture = model.layoutPref<string>(LID, 'wallpaper', 'theme')
  const { loadWallpaper } = model
  useEffect(() => { if (picture === 'custom') loadWallpaper() }, [picture, loadWallpaper])
  const setPref = (key: string, v: string | boolean | number) => model.setLayoutPref(LID, key, v)

  // View state only: which menu / panel is open.
  const [windowOpen, setWindowOpen] = useState(false)
  const [allOpen, setAllOpen] = useState(false)
  const [allTab, setAllTab] = useState<AllScreensTab>('menu')
  const [allFocus, setAllFocus] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const launcherRef = useRef<HTMLButtonElement>(null)

  // ── Menu bar in the host's top bar ──────────────────────────────────
  // The slot holds a stable, empty element and the menu bar is PORTALED into
  // it: the slot object never changes (the model is a new object on every
  // render), while the menu bar still renders with the live model.
  const hasChrome = useHasShellChrome()
  const chrome = useShellChrome()
  const [barHost, setBarHost] = useState<HTMLDivElement | null>(null)
  const slots = useMemo<LayoutTopBarSlots>(() => ({
    center: (
      <div ref={setBarHost} data-tw-shell-mac-menubar=""
        style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', alignSelf: 'stretch' }} />
    ),
  }), [])
  const [publishTries, setPublishTries] = useState(0)
  useEffect(() => {
    if (!hasChrome || chrome.center === slots.center || publishTries > PUBLISH_RETRIES) return undefined
    setTopBarSlots(slots)
    const t = setTimeout(() => setPublishTries((n) => n + 1), 60)
    return () => clearTimeout(t)
  }, [hasChrome, chrome.center, slots, setTopBarSlots, publishTries])
  useEffect(() => () => setTopBarSlots(null), [setTopBarSlots])

  // ── The desktop's size → the work area (less the Dock) ──────────────
  const rootRef = useRef<HTMLDivElement>(null)
  const deskRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })
  useLayoutEffect(() => {
    const el = rootRef.current
    if (!el) return undefined
    const read = () => setSize((p) => (p.w === el.clientWidth && p.h === el.clientHeight ? p : { w: el.clientWidth, h: el.clientHeight }))
    read()
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(read)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const top0 = hasChrome ? 0 : STRIP_HEIGHT
  const area = useMemo<Rect>(() => ({
    x: dockSide === 'left' ? DOCK_RESERVE : 0,
    y: top0,
    w: Math.max(0, size.w - (dockSide === 'bottom' ? 0 : DOCK_RESERVE)),
    h: Math.max(0, size.h - top0 - (dockSide === 'bottom' ? DOCK_RESERVE : 0)),
  }), [size.w, size.h, dockSide, top0, DOCK_RESERVE])

  // ── Windows ─────────────────────────────────────────────────────────
  const scope = model.storageScope
  const storedRef = useRef(loadWindows(scope))
  const zRef = useRef(1)
  const [wins, setWins] = useState<Record<string, WinState>>({})
  const openIds = model.openScreens.map((i) => i.id).join('|')
  const fresh = useCallback((id: string, index: number): WinState => {
    const st = storedRef.current[id]
    zRef.current += 1
    if (st && st.rect) return reflow({ ...st, z: zRef.current, min: !!st.min }, area)
    // The first window fills the desktop (and keeps filling it as the browser
    // resizes); later ones cascade over it.
    return index === 0
      ? { rect: tileRect('fill', area), tile: 'fill', restore: null, min: false, z: zRef.current }
      : { rect: defaultRect(area, index), tile: null, restore: null, min: false, z: zRef.current }
  }, [area])
  // Open screens ↔ windows: a newly opened screen gets a window, a closed one loses it.
  useEffect(() => {
    if (!size.w) return
    setWins((prev) => {
      const next: Record<string, WinState> = {}
      let changed = false
      model.openScreens.forEach((item, i) => {
        if (prev[item.id]) next[item.id] = prev[item.id]
        else { next[item.id] = fresh(item.id, Object.keys(next).length === 0 && i === 0 ? 0 : i); changed = true }
      })
      if (Object.keys(prev).some((id) => !next[id])) changed = true
      return changed ? next : prev
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openIds, size.w > 0])
  // The work area changed (browser resized, Dock moved): re-place every window.
  useEffect(() => {
    if (!size.w) return
    setWins((prev) => {
      let changed = false
      const next: Record<string, WinState> = {}
      for (const [id, w] of Object.entries(prev)) {
        const r = reflow(w, area)
        if (r !== w) changed = true
        next[id] = r
      }
      return changed ? next : prev
    })
  }, [area, size.w])
  // The front window is the current screen: raise it (and bring it back from the Dock).
  useEffect(() => {
    if (!current) return
    setWins((p) => {
      const w = p[current.id]
      if (!w) return p
      zRef.current += 1
      return { ...p, [current.id]: { ...w, z: zRef.current, min: false } }
    })
  }, [current?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!size.w) return
    storedRef.current = { ...storedRef.current, ...wins }
    saveWindows(scope, storedRef.current as Record<string, WinState>)
  }, [wins, scope, size.w])

  const stateOf = (item: RunNavItem, i: number): WinState => wins[item.id] || {
    rect: i === 0 ? tileRect('fill', area) : defaultRect(area, i), tile: i === 0 ? 'fill' : null,
    restore: null, min: false, z: 0,
  }
  const update = useCallback((id: string, fn: (w: WinState) => WinState) => {
    setWins((p) => (p[id] ? { ...p, [id]: fn(p[id]) } : p))
  }, [])
  const tile = useCallback((id: string, kind: TileKind) => update(id, (w) => ({
    ...w, rect: tileRect(kind, area), tile: kind, restore: w.tile ? w.restore : w.rect, min: false,
  })), [update, area])
  const restore = useCallback((id: string) => update(id, (w) => {
    const back = w.restore || { x: area.x + area.w * 0.15, y: area.y + area.h * 0.1, w: area.w * 0.7, h: area.h * 0.78 }
    return { ...w, rect: clampRect(back, area), tile: null, restore: null }
  }), [update, area])
  const arrangeAll = useCallback(() => {
    const visible = model.openScreens.filter((i) => !wins[i.id]?.min)
    const rects = arrange(visible.length, area)
    setWins((p) => {
      const next = { ...p }
      visible.forEach((item, i) => {
        const w = next[item.id]
        if (w) next[item.id] = { ...w, rect: rects[i], tile: null, restore: w.tile ? w.restore : w.rect }
      })
      return next
    })
  }, [model.openScreens, wins, area])
  const visibleOthers = (exceptId: string) => model.openScreens
    .filter((i) => i.id !== exceptId && !wins[i.id]?.min)
    .sort((a, b) => (wins[b.id]?.z || 0) - (wins[a.id]?.z || 0))
  const minimiseWindow = (item: RunNavItem) => {
    // An editor on screen goes to the Dock as its own thumbnail first.
    if (shownEditor && shownEditor.objName === item.obj_name && current?.id === item.id) {
      model.minimiseEditor(shownEditor.id)
      return
    }
    update(item.id, (w) => ({ ...w, min: true }))
    if (current?.id === item.id) {
      const next = visibleOthers(item.id)[0]
      if (next) model.open(next)
      else model.blur()
    }
  }
  const restoreWindow = (item: RunNavItem) => {
    update(item.id, (w) => ({ ...w, min: false }))
    model.open(item)
  }

  // Close All — as Option-click on the red button, the Window and Dock menus,
  // and Alt+Shift+W (outside a field, where that combination types a character).
  const closeAllWindows = useCallback(() => { model.closeAllScreens() }, [model])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.altKey && e.shiftKey && (e.code === 'KeyW' || e.key.toLowerCase() === 'w'))) return
      const t = e.target as HTMLElement | null
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return
      e.preventDefault()
      closeAllWindows()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [closeAllWindows])

  // Tile preview while a window is dragged to an edge.
  const [preview, setPreview] = useState<TileKind | null>(null)

  // "Open editors" (the cap notice): the Window menu lists them.
  useEffect(() => {
    if (!model.editorsListRequest) return
    if (model.fullScreen) model.setFullScreen(false)
    setAllOpen(false)
    setWindowOpen(true)
  }, [model.editorsListRequest]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setAllOpen(false) }, [current?.id])
  useEffect(() => { if (full) { setAllOpen(false); setWindowOpen(false) } }, [full])

  const openWindowMenu = useCallback((open: boolean) => {
    setWindowOpen(open)
    if (open) setAllOpen(false)
  }, [])
  const toggleAll = useCallback(() => {
    setAllOpen((v) => !v)
    setAllTab('menu')
    setAllFocus(null)
    setWindowOpen(false)
  }, [])
  const closeAll = useCallback((refocus: boolean) => {
    setAllOpen(false)
    setAllFocus(null)
    if (refocus) launcherRef.current?.focus()
  }, [])
  const cw = current ? wins[current.id] : undefined
  const windowActions: WindowAction[] = [
    { key: 'minimise-window', label: 'Minimise window', disabled: !current, run: () => { if (current) minimiseWindow(current) } },
    { key: 'tile-fill', label: 'Fill', disabled: !current, run: () => { if (current) tile(current.id, 'fill') } },
    { key: 'tile-left', label: `Move to ${TILE_LABELS.left.toLowerCase()}`, disabled: !current, run: () => { if (current) tile(current.id, 'left') } },
    { key: 'tile-right', label: `Move to ${TILE_LABELS.right.toLowerCase()}`, disabled: !current, run: () => { if (current) tile(current.id, 'right') } },
    { key: 'arrange', label: 'Arrange all windows', disabled: model.openScreens.length < 2, run: arrangeAll },
    { key: 'restore-size', label: 'Return to previous size', disabled: !cw?.tile, run: () => { if (current) restore(current.id) } },
    { key: 'close-all', label: 'Close All (Alt+Shift+W)', disabled: model.openScreens.length === 0, run: closeAllWindows },
    { key: 'settings', label: 'Desktop & Dock settings…', run: () => setSettingsOpen(true) },
  ]
  const menuBar = (
    <MacMenuBar model={model} windowOpen={windowOpen} setWindowOpen={openWindowMenu} windowActions={windowActions} />
  )

  const minimised = model.openScreens.filter((i) => wins[i.id]?.min)
  const anyVisible = model.openScreens.some((i) => !wins[i.id]?.min)
  const reopen = !anyVisible
    ? (minimised[minimised.length - 1] || model.openScreens[model.openScreens.length - 1] || model.recent[0]?.item || null)
    : null
  const darkOnAccent = `color-mix(in srgb, ${accent} 82%, #000)`

  const desktopCard = !full && !anyVisible ? (
    <div style={{
      position: 'absolute', left: area.x, top: area.y, width: area.w, height: area.h, zIndex: 5,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box',
    }}>
      <div style={{
        width: 'min(440px, 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center',
        paddingBottom: reopen ? 24 : 0, borderRadius: 16, background: glass(S.panel, 84),
        border: `1px solid ${S.border}`, boxShadow: `0 18px 40px ${S.shadow}`,
        backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
      }}>
        <EmptyPane model={model} appTitle={appTitle} accent={themeColor}
          message={model.roots.length ? emptyPaneMessage : emptyNavMessage} />
        {reopen && !model.missingObjName && (
          <button type="button" onClick={() => restoreWindow(reopen)} {...tw('action', 'shell-mac-reopen', { obj: reopen.obj_name })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, height: 40, padding: '0 16px', marginTop: -8,
              border: 'none', borderRadius: 9, background: darkOnAccent, color: '#fff', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 14, fontWeight: 600, maxWidth: 'calc(100% - 32px)',
            }}>
            <NavIcon node={reopen} size={16} />
            <span className="tw-sh-clip">Reopen {reopen.title}</span>
          </button>
        )}
      </div>
    </div>
  ) : null

  const previewRect = preview ? tileRect(preview, area) : null
  // Stacking order as RANKS (0 … n-1): the raw counter only grows, and windows
  // must stay below the Dock and the panels whatever was clicked how often.
  const rank = new Map(model.openScreens
    .map((i) => [i.id, wins[i.id]?.z || 0] as const)
    .sort((a, b) => a[1] - b[1])
    .map(([id], r) => [id, r]))

  return (
    <div ref={rootRef} style={{
      position: 'relative', flex: 1, width: '100%', height: '100%', minHeight: 0, overflow: 'hidden',
      isolation: 'isolate',
      background: full ? S.bg : wallpaperCss(picture, accent, model.wallpaper.image),
    }}>
      <style>{MAC_CSS}</style>
      {!full && picture === 'theme' && (
        // Two soft shapes on the default picture, as on the board — decoration only.
        <div aria-hidden style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          <div style={{
            position: 'absolute', right: -180, bottom: -260, width: 820, height: 820, borderRadius: '50%',
            background: `color-mix(in srgb, ${accent} 9%, transparent)`,
          }} />
          <div style={{
            position: 'absolute', left: -140, top: 160, width: 520, height: 520, borderRadius: 120,
            transform: 'rotate(18deg)', background: `color-mix(in srgb, ${S.panel} 26%, transparent)`,
          }} />
        </div>
      )}
      {!full && !hasChrome && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: STRIP_HEIGHT, zIndex: 25,
          display: 'flex', alignItems: 'center', padding: '0 8px', boxSizing: 'border-box',
          background: glass(S.panel, 78), borderBottom: `1px solid ${S.border}`,
          backdropFilter: 'blur(24px) saturate(180%)', WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        }}>
          {menuBar}
        </div>
      )}
      <div ref={deskRef} aria-label="Desktop" role="region" style={{ position: 'absolute', inset: 0 }}>
        {desktopCard}
        {size.w > 0 && model.openScreens.map((item, i) => {
          const st = { ...stateOf(item, i), z: rank.get(item.id) ?? 0 }
          const isCurrent = current?.id === item.id
          return (
            <MacWindow
              key={item.id}
              model={model}
              item={item}
              state={st}
              area={area}
              isCurrent={isCurrent}
              full={full && isCurrent}
              hidden={st.min || (full && !isCurrent)}
              accent={accent}
              compact={compact}
              shownEditor={shownEditor}
              renderObject={renderObject}
              headerExtras={headerExtras}
              desktopEl={() => deskRef.current}
              onFocus={() => { if (!isCurrent) model.open(item) }}
              onCommit={(next) => update(item.id, () => next)}
              onTile={(k) => tile(item.id, k)}
              onRestore={() => restore(item.id)}
              onArrange={arrangeAll}
              onMinimise={() => minimiseWindow(item)}
              onCloseAll={closeAllWindows}
              onPreview={setPreview}
            />
          )
        })}
        {previewRect && (
          // Where the dragged window will tile.
          <div aria-hidden style={{
            position: 'absolute', left: previewRect.x, top: previewRect.y, width: previewRect.w, height: previewRect.h,
            zIndex: 9000, borderRadius: 12, pointerEvents: 'none', transition: 'all .12s ease',
            background: glass(S.accent, 18), border: `2px solid ${glass(S.accent, 70)}`,
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
          }} />
        )}
      </div>
      {!full && (
        <Dock model={model} accent={accent} allOpen={allOpen} onToggleAll={toggleAll} launcherRef={launcherRef}
          side={dockSide} magnify={magnify} size={dockSize}
          onSide={(s) => setPref('dockSide', s)} onMagnify={(on) => setPref('magnify', on)}
          onSize={(px) => setPref('dockSize', px)} onCloseAll={closeAllWindows}
          onSettings={() => setSettingsOpen(true)}
          minimised={minimised} onRestoreWindow={restoreWindow} />
      )}
      {!full && allOpen && (
        <AllScreens model={model} tab={allTab} setTab={setAllTab} onClose={closeAll} launcherRef={launcherRef}
          focusNodeId={allFocus} emptyNavMessage={emptyNavMessage} side={dockSide} />
      )}
      {settingsOpen && (
        <MacSettings model={model} accent={accent}
          dockSide={dockSide} setDockSide={(s) => setPref('dockSide', s)}
          dockSize={dockSize} setDockSize={(px) => setPref('dockSize', px)}
          magnify={magnify} setMagnify={(on) => setPref('magnify', on)}
          picture={picture} setPicture={(id) => setPref('wallpaper', id)}
          onClose={() => setSettingsOpen(false)} />
      )}
      {!full && hasChrome && barHost && createPortal(menuBar, barHost)}
    </div>
  )
}
